import React from 'react'
import {
  Box,
  Button,
  List,
  ListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import type { CommentDay, CommentReport, ReconWindow } from '../../../hooks/socialdash/useSkyReconciliation'
import {
  commentDayLabel,
  commentLedger,
  dayAfterImport,
  daysSummary,
  diffLabel,
  fmtInt,
  ledgerToImport,
  platformDaysLine,
  signed,
  utcDayLabel,
} from '../reconciliationModel'

/**
 * The comments ledger (design/2026-10-19-sky-reconciliation-page-redesign.md,
 * M2): Sprinklr beside Platinum per platform, X as one heading with its kinds
 * under it, a difference and a percent, and under each platform how many of its
 * days match. What Sprinklr counts that is not a comment comparison (Sky's own
 * posts on each platform, platforms Platinum doesn't cover) sits apart in a footer, in plain
 * text. Then the days that differ: a day the import has not finished is marked
 * as such with no Sprinklr call; any other opens its reasons on "See why",
 * which asks Sprinklr for that day's messages.
 */
export interface CommentsSectionProps {
  report: CommentReport
  window: Pick<ReconWindow, 'startMs' | 'endMs'>
  /** How many platforms are compared (for "of N platform-days"). */
  platforms: number
  frontierMs?: number
  onCheckDay: (day: CommentDay) => void
}

const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const
/** The absolute difference gives way to the percent on a phone. */
const wideOnly = { display: { xs: 'none', sm: 'table-cell' } } as const

/** "LinkedIn, Threads and YouTube". */
function andList(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

const CommentsSection: React.FC<CommentsSectionProps> = ({ report, window, platforms, frontierMs, onCheckDay }) => {
  const theme = useTheme()
  const phone = useMediaQuery(theme.breakpoints.down('sm'))
  const ledger = commentLedger(report.lines)
  const days = report.days ?? []
  const summary = daysSummary(days, platforms, window, frontierMs)
  const imported = ledgerToImport(ledger.rows, report, frontierMs)
  const later = (n: number) => (n === 0 ? '—' : fmtInt(n))

  const whyFor = (d: CommentDay) =>
    dayAfterImport(d.dayMs, frontierMs) ? (
      <Typography component="span" variant="body2" color="text.secondary">Not imported yet</Typography>
    ) : (
      <Button size="small" color="info" onClick={() => onCheckDay(d)} fullWidth={phone}>See why</Button>
    )

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }} data-testid="recon-comments">
      <Typography variant="h6">Comments</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Each comment by its own UTC day. Platinum counts every comment it imported from Sprinklr, coded or not.
      </Typography>
      {imported.hasLater && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }} data-testid="recon-comments-basis">
          Compared over the days Platinum has imported, to {utcDayLabel(imported.lastFullDayMs)}. The last column is Sprinklr&apos;s own
          count for the later days; they join the comparison once Platinum imports them, so any gap shows then.
        </Typography>
      )}
      <Table size="small" data-testid="recon-comment-lines" sx={{ tableLayout: 'auto' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ pl: 0 }}>Platform</TableCell>
            <TableCell align="right">Sprinklr</TableCell>
            <TableCell align="right">Platinum</TableCell>
            <TableCell align="right" sx={wideOnly}>Difference</TableCell>
            <TableCell align="right" sx={imported.hasLater ? undefined : { pr: 0 }}>%</TableCell>
            {imported.hasLater && <TableCell align="right" sx={{ ...wideOnly, pr: 0 }}>Not imported yet (Sprinklr)</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {imported.rows.map((r) => (
            <React.Fragment key={`${r.platform}|${r.kind}`}>
            <TableRow data-testid={r.child ? undefined : `recon-comment-line-${r.platform}`} sx={phone && !r.child ? { '& > *': { borderBottom: 0 } } : undefined}>
              <TableCell component="th" scope="row" sx={{ pl: r.child ? 2.5 : 0, verticalAlign: 'top' }}>
                <Typography variant="body2" sx={{ fontWeight: r.child ? 400 : 600 }} color={r.child ? 'text.secondary' : undefined}>
                  {r.label}
                </Typography>
                {!r.child && !phone && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {platformDaysLine(days, r.platform, window, frontierMs)}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right" sx={{ ...num, verticalAlign: 'top' }}>{fmtInt(r.sky)}</TableCell>
              <TableCell align="right" sx={{ ...num, verticalAlign: 'top' }}>{fmtInt(r.ours)}</TableCell>
              <TableCell align="right" sx={{ ...num, ...wideOnly, verticalAlign: 'top' }}>{signed(r.ours - r.sky)}</TableCell>
              <TableCell align="right" sx={{ ...num, verticalAlign: 'top', ...(imported.hasLater ? {} : { pr: 0 }) }}>{diffLabel(r.ours, r.sky)}</TableCell>
              {imported.hasLater && (
                <TableCell align="right" sx={{ ...num, ...wideOnly, pr: 0, verticalAlign: 'top', color: 'text.secondary' }}>{later(r.later)}</TableCell>
              )}
            </TableRow>
            {phone && !r.child && (
              // On a phone the "why" line takes the row's full width under its figures.
              <TableRow>
                <TableCell colSpan={4} sx={{ pl: 0, pt: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    {platformDaysLine(days, r.platform, window, frontierMs)}
                    {r.later !== 0 && ` · ${fmtInt(r.later)} in Sprinklr on days not imported yet`}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            </React.Fragment>
          ))}
          <TableRow sx={{ '& > *': { borderTop: 2, borderTopColor: 'divider', borderBottom: 0 } }}>
            <TableCell component="th" scope="row" sx={{ fontWeight: 600, pl: 0 }}>Total</TableCell>
            <TableCell align="right" sx={{ ...num, fontWeight: 600 }}>{fmtInt(imported.total.sky)}</TableCell>
            <TableCell align="right" sx={{ ...num, fontWeight: 600 }}>{fmtInt(imported.total.ours)}</TableCell>
            <TableCell align="right" sx={{ ...num, ...wideOnly, fontWeight: 600 }}>{signed(imported.total.ours - imported.total.sky)}</TableCell>
            <TableCell align="right" sx={{ ...num, fontWeight: 600, ...(imported.hasLater ? {} : { pr: 0 }) }}>{diffLabel(imported.total.ours, imported.total.sky)}</TableCell>
            {imported.hasLater && (
              <TableCell align="right" sx={{ ...num, ...wideOnly, fontWeight: 600, pr: 0 }}>{later(imported.total.later)}</TableCell>
            )}
          </TableRow>
        </TableBody>
      </Table>

      {(ledger.skyOwnPosts.length > 0 || ledger.notCovered.length > 0) && (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }} data-testid="recon-comment-outside">
          <Typography variant="subtitle2">Also in Sprinklr, outside the comparison</Typography>
          {ledger.skyOwnPosts.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary">
                Sprinklr&apos;s comment count includes Sky&apos;s own posts; Platinum counts them with posts.
              </Typography>
              <Box component="ul" sx={{ m: 0, mb: 0.5, pl: 2.5 }}>
                {ledger.skyOwnPosts.map((o) => (
                  <Typography key={o.platform} component="li" variant="body2" color="text.secondary" data-testid="recon-own-posts">
                    {o.label}: {fmtInt(o.sky)} of Sky&apos;s own posts (counted as posts, not comments)
                  </Typography>
                ))}
              </Box>
            </>
          )}
          {ledger.notCovered.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              Platinum covers Facebook, Instagram, TikTok and X. Sprinklr also holds {fmtInt(ledger.notCoveredTotal)} comments
              on {andList(ledger.notCovered.map((p) => p.label))}, which are outside Platinum&apos;s scope.
            </Typography>
          )}
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ mt: 3, fontWeight: 600 }}>Days that differ</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} data-testid="recon-days-summary">
        {summary.text}
        {days.length > 0 && ' See why lists a day’s messages on both sides, each with its reason.'}
      </Typography>
      {days.length > 0 && (phone ? (
        <List disablePadding data-testid="recon-comment-days" sx={{ maxHeight: 480, overflowY: 'auto' }}>
          {days.map((d) => (
            <ListItem key={`${d.dayMs}|${d.platform}|${d.kind}`} disableGutters divider sx={{ display: 'block', py: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {utcDayLabel(d.dayMs)} · {commentDayLabel(d.platform, d.kind)}
              </Typography>
              <Typography variant="body2" sx={num} color="text.secondary">
                {fmtInt(d.sky)} → {fmtInt(d.ours)} · {signed(d.ours - d.sky)}
              </Typography>
              <Box sx={{ mt: 0.5 }}>{whyFor(d)}</Box>
            </ListItem>
          ))}
        </List>
      ) : (
        <Box sx={{ maxHeight: 480, overflowY: 'auto' }}>
          <Table size="small" stickyHeader data-testid="recon-comment-days">
            <TableHead>
              <TableRow>
                <TableCell>Day (UTC)</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell align="right">Sprinklr</TableCell>
                <TableCell align="right">Platinum</TableCell>
                <TableCell align="right">Difference</TableCell>
                <TableCell align="right">Why</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {days.map((d) => (
                <TableRow key={`${d.dayMs}|${d.platform}|${d.kind}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{utcDayLabel(d.dayMs)}</TableCell>
                  <TableCell>{commentDayLabel(d.platform, d.kind)}</TableCell>
                  <TableCell align="right" sx={num}>{fmtInt(d.sky)}</TableCell>
                  <TableCell align="right" sx={num}>{fmtInt(d.ours)}</TableCell>
                  <TableCell align="right" sx={num}>{signed(d.ours - d.sky)}</TableCell>
                  <TableCell align="right">{whyFor(d)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}
    </Paper>
  )
}

export default CommentsSection
